/**
 * Type definitions for AskAsper Agentic Platform Components
 * These types are designed to be compatible with the upcoming agentic platform integration
 */

import { ReactNode } from 'react';
import type { CaseItem, CreateCaseSaveData } from './Modals/CreateCaseModal.types';
import type { AgenticHtmlChartFields } from '../../shared/EChartsHtmlChart';

export type {
    AgenticEChartsChartAssets,
    AgenticEChartsChartRender,
    AgenticHtmlChartFields,
} from '../../shared/EChartsHtmlChart';

export type AgenticChartEnvelope = AgenticHtmlChartFields & {
    viz_data: any;
    task_id?: string;
    task_name?: string;
    scenario_link?: string;
};

// ===== Breadcrumb Types =====
export interface BreadcrumbItem {
    label: string;
    href?: string;
    icon?: ReactNode;
}

export interface AgenticTopBarProps {
    breadcrumbs?: BreadcrumbItem[];
    showNotification?: boolean;
    notificationCount?: number;
    onNotificationClick?: () => void;
    onBreadcrumbClick?: (item: BreadcrumbItem, index: number) => void;
    /** Callback when the Home icon (first breadcrumb) is clicked */
    onHomeClick?: () => void;

    // Menu toggle (from AskAsperHeader)
    onMenuToggle?: () => void;

    // View mode (from AskAsperHeader)
    viewMode?: 'list' | 'presentation';
    onViewModeChange?: (mode: 'list' | 'presentation') => void;
    showViewModeToggle?: boolean;

    // Action buttons (from AskAsperHeader)
    onCommentClick?: () => void;
    onCreateCaseClick?: () => void;
    showCommentButton?: boolean;
    showCreateCaseButton?: boolean;

    // DotsGrid / Tool Panel (from AskAsperHeader)
    onDotsGridClick?: () => void;
    showDotsGridButton?: boolean;
    toolPanelOpen?: boolean;
    onToolPanelOpenChange?: (open: boolean) => void;
    /** Callback when Export format is selected from the tool panel popup (PDF, PPT, Excel, Word) */
    onExportClick?: (format: 'pdf' | 'ppt' | 'xlsx' | 'docx') => void;
    onShareClick?: () => void;
    onPlaceholderClick?: () => void;
    onToolPanelSearch?: (value: string) => void;

    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Sidebar Types =====
export interface ConversationThread {
    id: number;
    thread_id: string;
    thread_name: string;
    user_id: string;
    created_on: string;
    is_pinned: boolean | null;
    pin_no: number | null;
}

export interface UserProfile {
    name: string;
    avatar?: string;
}

export interface AgenticSidebarProps {
    /** List of conversation threads to display */
    threads?: ConversationThread[];
    /** Whether threads are loading - shows animated loading bar in thread list area */
    threadsLoading?: boolean;
    /** Currently active thread ID */
    activeThreadId?: string;
    /** User profile information */
    user?: UserProfile;
    /** Whether the sidebar is collapsed */
    collapsed?: boolean;
    /** Controlled collapsed state - if provided, component becomes controlled */
    defaultCollapsed?: boolean;
    /** Callback when "New Chat" is clicked */
    onNewChat?: () => void;
    /** Callback when "Search Chats" is clicked */
    onSearchChats?: () => void;
    /** Callback when the search-chats input is cleared via the inline X icon */
    onSearchChatsClear?: () => void;
    /** Callback when a thread is selected */
    onThreadSelect?: (threadId: string) => void;
    /** Callback when thread name is edited */
    onThreadEdit?: (threadId: string, newName: string) => void;
    /** Callback when a thread is deleted */
    onThreadDelete?: (threadId: string) => void;
    /** Callback when sidebar toggle is clicked - receives new collapsed state */
    onToggleSidebar?: (collapsed: boolean) => void;
    /** Callback when user profile is clicked */
    onUserClick?: () => void;
    /** Whether to hide the header (logo and title) - used when sidebar is below navbar */
    hideHeader?: boolean;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Welcome Header Types =====
export interface AgenticWelcomeHeaderProps {
    userName?: string;
    greeting?: string;
    subtext?: string;
    showLogo?: boolean;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Quick Action Types =====
export interface QuickAction {
    id: string;
    label: string;
    fullQuestion?: string; // Full question text shown in tooltip and populated in input on click
    icon?: ReactNode;
    iconType?: 'trend' | 'portfolio' | 'simulation' | 'project' | 'report';
}

export interface AgenticQuickActionsProps {
    actions?: QuickAction[];
    onActionClick?: (action: QuickAction) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Slash-command Lookup Types =====
/** Distinct (column_name, entity_name) pair returned by the host's lookup-columns API.
 *  `entity_name` is the user-facing value used to power the `/` slash-command suggestion
 *  dropdown; `column_name` is sent back to the backend alongside the message. */
export interface LookupItem {
    column_name: string;
    entity_name: string;
}

/** Portfolio-filter entity type (dimension level). When the user types `/`,
 *  a picker lists each type by `label`. After they pick one, tier 2 searches
 *  within that type's items. Typing after `/` also searches values across types.
 *
 *  - `id`: stable identifier (normalized column key, e.g. `'brand'`, `'time period'`).
 *  - `label`: human-readable label shown in the entity-type list.
 *  - `items`: static items for the type (e.g. Time Period). When omitted, the
 *    type falls back to runtime `lookupItems` filtered by column.
 *  - `dimensionGroupId`: when set, picking this row drills into the named
 *    host dimension group (Product / Market) instead of opening value search.
 */
export interface LookupCategory {
    id: string;
    label: string;
    items?: LookupItem[];
    dimensionGroupId?: string;
}

/**
 * Host-provided Product / Market (or custom) buckets for the `/` picker.
 * When passed, bare `/` shows Time Period then each group in array order;
 * drilling a group lists `entityTypes` in config order. Types present in
 * `lookupItems` but absent from every `entityTypes` array are hidden.
 */
export interface LookupDimensionGroup {
    /** Stable id, e.g. `product`, `market`. */
    id: string;
    /** Display label on the initial `/` list, e.g. `Product`, `Market`. */
    label: string;
    /**
     * Entity-type column names in display order.
     * Matched against `LookupItem.column_name` (case / whitespace normalized).
     */
    entityTypes: string[];
}

/** Optional props consumed by the search input + left panel for the `/` slash-command lookup.
 *  Hosts that don't pass these props get the existing input behaviour unchanged. */
export interface LookupIntegrationProps {
    /** All available lookup items (lazy-loaded by the host on first focus). */
    lookupItems?: LookupItem[];
    /** Lookups the user has already inserted as `/entity_name` tokens in the input. */
    selectedLookups?: LookupItem[];
    /** Called when the selected-lookups list changes (selection added or token removed). */
    onLookupsChange?: (next: LookupItem[]) => void;
    /** Called the first time the textarea receives focus, so the host can lazy-load the list. */
    onLoadLookups?: () => void;
    /** Optional ordered entity-type list for the portfolio filter. When omitted,
     *  types are derived from `lookupItems` (Time Period first, then hierarchy order). */
    lookupCategories?: LookupCategory[];
    /**
     * Optional Product / Market (Dimension 1 / 2) buckets. When omitted, `/`
     * falls back to the flat Time Period + entity-type list. When set, bare `/`
     * shows Time Period then each group; unmapped entity types are hidden.
     */
    lookupDimensionGroups?: LookupDimensionGroup[];
    /** Analytics callback for composer slash-command mixpanel events. */
    onTrackEvent?: import('./utils/mixpanelEvents').MixpanelTrackEventCallback;
    /** Thread id included in slash-command analytics payloads when known. */
    analyticsThreadId?: string;
    /** Session id included in slash-command analytics payloads (generated per layout mount when omitted). */
    analyticsSessionId?: string;
}

// ===== Search Input Types =====
export interface AgenticSearchInputProps extends LookupIntegrationProps {
    value?: string;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
    onQuestionBankClick?: () => void;
    onSaveAsCustom?: (value: string) => void;
    /** Callback fired the first time the textarea receives focus in a focus session (used by hosts for analytics). */
    onFocus?: () => void;
    placeholder?: string;
    isLoading?: boolean;
    disabled?: boolean;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Landing Content Types =====
export interface AgenticLandingContentProps extends LookupIntegrationProps {
    userName?: string;
    greeting?: string;
    subtext?: string;
    quickActions?: QuickAction[];
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    onQuickActionClick?: (action: QuickAction) => void;
    onQuestionBankClick?: () => void;
    onSaveAsCustom?: (value: string) => void;
    /** Callback when a question is selected from the question bank modal (includes the source category id, e.g. 'saved'). */
    onQuestionSelect?: (question: AgenticQuestion, categoryId: string) => void;
    /** Callback fired the first time the landing search input receives focus (used by hosts for analytics). */
    onSearchInputFocus?: () => void;
    /** Callback when the question-bank modal search input runs a (debounced) non-empty search. */
    onQuestionBankSearch?: (value: string) => void;
    /** Callback when the question-bank modal search input is cleared after being non-empty. */
    onQuestionBankSearchClear?: () => void;
    /** Callback when a section/category is clicked in the question-bank modal sidebar. */
    onQuestionBankSectionClick?: (sectionId: string, sectionName: string) => void;
    /** Question bank categories in API format: { Category, Questions: [{ QuestionID, Question }] }[] */
    questionBankCategories?: QuestionBankCategoryFromAPI[];
    /** Custom/saved questions for the Question Bank (when provided, parent controls the list) */
    customQuestions?: AgenticQuestion[];
    /** Callback when a saved question is edited */
    onEditCustomQuestion?: (question: {
        id?: string | number;
        text?: string;
        QuestionID?: number;
        Question?: string;
    }) => void;
    /** Callback when a saved question is deleted */
    onDeleteCustomQuestion?: (questionId: string) => void;
    isLoading?: boolean;
    /** When true (e.g. disableNavbar mode), shows the sidebar toggle (hamburger) to the left of the content */
    showSidebarToggleInPanel?: boolean;
    /** Callback when sidebar toggle is clicked - used when showSidebarToggleInPanel is true */
    onSidebarToggleInPanel?: () => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Main Layout Types =====
export interface AgenticMainLayoutProps extends LookupIntegrationProps {
    // Layout configuration
    sidebarCollapsed?: boolean;
    showSidebar?: boolean;
    showTopBar?: boolean;
    /** When true, hides the top bar and moves the sidebar toggle (hamburger) into the content area. Use when the host app has its own global navbar. */
    disableNavbar?: boolean;
    /** Optional footer height (px). When provided, the layout height is reduced so it sits above the consumer's bottom footer. */
    footerHeight?: number;

    // User info
    userName?: string;

    // Sidebar - threads and related
    /** List of conversation threads to display in the sidebar */
    threads?: ConversationThread[];
    /** Whether sidebar threads are loading - shows animated loading bar in thread list area */
    threadsLoading?: boolean;
    /** Currently active thread ID */
    activeThreadId?: string;
    /** User profile for sidebar (overrides userName for display/avatar) */
    user?: UserProfile;
    /** Whether the sidebar is in collapsed (icon-only) mode when expanded */
    sidebarDefaultCollapsed?: boolean;
    /** Whether to hide the sidebar header (logo and title) */
    sidebarHideHeader?: boolean;

    // Breadcrumb
    breadcrumbs?: BreadcrumbItem[];

    // Quick actions
    quickActions?: QuickAction[];

    // Search state
    searchValue?: string;
    isLoading?: boolean;

    // Event handlers
    onSidebarToggle?: () => void;
    onNewChat?: () => void;
    onHistoryClick?: () => void;
    /** Callback when a thread is selected in the sidebar */
    onThreadSelect?: (threadId: string) => void;
    /** Callback when thread name is edited in the sidebar */
    onThreadEdit?: (threadId: string, newName: string) => void;
    /** Callback when a thread is deleted in the sidebar */
    onThreadDelete?: (threadId: string) => void;
    /** Callback when user profile is clicked in the sidebar */
    onUserClick?: () => void;
    onNotificationClick?: () => void;
    onBreadcrumbClick?: (item: BreadcrumbItem, index: number) => void;
    /** Callback when the Home icon (first breadcrumb) is clicked */
    onHomeClick?: () => void;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    onQuickActionClick?: (action: QuickAction) => void;
    onQuestionBankClick?: () => void;
    onSaveAsCustom?: (value: string) => void;
    /** Question bank categories in API format: { Category, Questions: [{ QuestionID, Question }] }[] */
    questionBankCategories?: QuestionBankCategoryFromAPI[];
    /** Custom/saved questions for the Question Bank */
    customQuestions?: AgenticQuestion[];
    /** Callback when a saved question is edited */
    onEditCustomQuestion?: (question: {
        id?: string | number;
        text?: string;
        QuestionID?: number;
        Question?: string;
    }) => void;
    /** Callback when a saved question is deleted */
    onDeleteCustomQuestion?: (questionId: string) => void;

    /** Callback when Export format is selected from the tool panel popup (PDF, PPT, Excel, Word) */
    onExportClick?: (format: 'pdf' | 'ppt' | 'xlsx' | 'docx') => void;

    // ===== Mixpanel Analytics =====
    /**
     * Analytics callback. Receives `(eventName, eventProperty)` where `eventName`
     * is one of the constants in `utils/mixpanelEvents.ts`. Matches the host
     * app's `trackEvent(eventName, eventProperty)` signature.
     */
    onTrackEvent?: import('./utils/mixpanelEvents').MixpanelTrackEventCallback;

    // Children for custom content
    children?: ReactNode;

    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Default Quick Actions =====
export const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
    {
        id: 'analyse-trend',
        label: 'Analyse Trend',
        fullQuestion: 'How can I analyse the trend in my portfolio performance over the last quarter?',
        iconType: 'trend',
    },
    {
        id: 'track-portfolio',
        label: 'Track Portfolio',
        fullQuestion: 'Can you help me track my portfolio performance and identify key drivers of change?',
        iconType: 'portfolio',
    },
    {
        id: 'run-simulation',
        label: 'Run a Simulation',
        fullQuestion: 'I would like to run a simulation to see how different scenarios would impact my portfolio.',
        iconType: 'simulation',
    },
    {
        id: 'create-project',
        label: 'Create a Project',
        fullQuestion: 'Help me create a new project to analyze and track specific business metrics.',
        iconType: 'project',
    },
    {
        id: 'generate-report',
        label: 'Generate a Report',
        fullQuestion: 'Can you generate a comprehensive report summarizing my portfolio performance and key insights?',
        iconType: 'report',
    },
];

/** Default placeholder for Ask Asper input bars (landing page and conversation panel). */
export const DEFAULT_INPUT_PLACEHOLDER = 'Ask me anything... Use / to browse your portfolio';

// ===== Default Breadcrumbs =====
export const DEFAULT_BREADCRUMBS: BreadcrumbItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Ask Asper' },
];

// ===== Question Bank Types =====
export interface AgenticQuestion {
    id: string;
    text: string;
}

/** API format for question bank categories - consumed via questionBankCategories prop */
export interface QuestionBankCategoryFromAPI {
    Category: string;
    Questions: Array<{
        QuestionID: number;
        Question: string;
    }>;
}

export interface AgenticQuestionCategory {
    id: string;
    name: string;
    displayName: string;
    description: string;
    questions: AgenticQuestion[];
}

export interface AgenticQuestionBankProps {
    categories?: AgenticQuestionCategory[];
    savedQuestions?: AgenticQuestion[];
    onClose?: () => void;
    onQuestionSelect?: (question: AgenticQuestion, categoryId: string) => void;
    /** Callback fired when the user clicks a section/category in the question-bank sidebar
     *  (including the "All Questions" and "Saved Questions" sections). Used by hosts for
     *  analytics (e.g. QUESTION_BANK_SECTION_CLICK). `sectionName` is the section's display name. */
    onSectionClick?: (sectionId: string, sectionName: string) => void;
    onEditSavedQuestion?: (question: {
        id?: string | number;
        text?: string;
        QuestionID?: number;
        Question?: string;
    }) => void;
    onDeleteSavedQuestion?: (questionId: string) => void;
    /** Callback fired (debounced) when the user runs a non-empty search inside the question bank. */
    onSearch?: (value: string) => void;
    /** Callback fired when the user clears a previously non-empty search inside the question bank. */
    onSearchClear?: () => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Chat message format (aligned with transformed API / SSE stream) =====
export interface AgenticPlanningTask {
    task_id?: string;
    task_name?: string;
    description?: string;
}

export interface AgenticIntermediateStep {
    step: string;
    status: string;
    content: string | AgenticPlanningTask[] | unknown; // string, or array of planning tasks for Planning step
    timestamp: number;
}

export interface AgenticFollowupQuestion {
    keyword?: string;
    question?: string;
}

/** Task output from agentic runs - contains result, sql_db_output, formulae per task (from additional_kwargs.tasks) */
export interface AgenticTaskOutput {
    task?: string;
    sql_skills_used?: string[];
    result?: {
        /** Query text for this task - shown in phase card middle strip (e.g. "What is the revenue trend?") */
        query?: string;
        overview?: string;
        key_takeaways?: string[];
        /** Assumptions list - backend sends as string[] */
        assumptions?: string | string[];
        /** Formulae list - backend sends as string[] */
        formulae?: string | string[];
        key_findings?: string[];
    };
    success?: boolean;
    sql_db_output?: unknown[][];
    viz_state?: boolean;
    visualizations?: AgenticChartEnvelope[];
}

/** Task from agentic runs (additional_kwargs.tasks) - received during real streaming.
 *  task_output accepts unknown for consumer compatibility (e.g. when consuming apps use task_output?: unknown). */
export interface AgenticTask {
    task_id?: string;
    task_name?: string;
    /** Live status line during execution (e.g. task_start) — used for phase card copy */
    content?: string;
    status?: string;
    task_output?: AgenticTaskOutput | unknown;
}

/** Message shape from transformChatHistory(history) or from processSSEMessage (loading). */
export interface AgenticChatMessage {
    id?: number | string;
    thread_id?: string;
    run_id?: string;
    type?: string;
    content?: string;
    user_input?: string;
    question?: string;
    /** Consolidated question label after clarification — used for the overall question title on the right panel */
    summarized_title?: string;
    created_on?: string;
    thumbs_up?: boolean | null;
    subjective_answer?: string | null;
    tool_calls?: unknown[];
    /** Final response summary - overview and key takeaways from AI response */
    overview?: string;
    key_takeaways?: string[];
    /** Global key findings for the message - shown in Key Findings card on right panel */
    key_findings?: string[];
    additional_kwargs?: {
        intermediate_steps?: AgenticIntermediateStep[];
        charts?: AgenticChartEnvelope[];
        followup_questions?: string; // JSON string of AgenticFollowupQuestion[]
        /** Task completion events from agentic runs - contains visualizations, key_takeaways, formulae per task */
        task_complete_events?: AgenticTaskCompleteEvent[];
        /** Tasks from agentic runs - received during real streaming, contains task_output with result, formulae, sql_db_output per task */
        tasks?: AgenticTask[];
        /** Overview (one-liner) - shown above followup questions in left panel bottom response */
        overview?: string;
        /** Key takeaways - shown in right panel Analysis Summary card ("Key Findings") */
        key_takeaways?: string[];
        /** Allow extra properties from consuming apps (e.g. interrupt, etc.) */
        [key: string]: unknown;
    };
    /** Set to true when the SSE 'ai' event (final response) has been received — used to disable typewriter */
    receivedAiEvent?: boolean;
}

/** Task completion event - contains task output and visualizations */
export interface AgenticTaskCompleteEvent {
    type?: string;
    task_id?: string;
    task_name?: string;
    task_output?: {
        task?: string;
        result?: {
            /** Query text for this task - shown in phase card middle strip (e.g. "What is the revenue trend?") */
            query?: string;
            overview?: string;
            key_takeaways?: string[];
            /** Assumptions list - backend sends as string[] */
            assumptions?: string | string[];
            /** Formulae list - backend sends as string[] */
            formulae?: string | string[];
            key_findings?: string[];
        };
        success?: boolean;
        viz_state?: boolean;
        /** Visualizations (charts, tables) - backend may send inside task_output */
        visualizations?: AgenticChartEnvelope[];
    };
    run_id?: string;
    visualizations?: AgenticChartEnvelope[];
}

// ===== Left Panel Types =====
export interface AgenticConversationMessage {
    id?: string | number;
    type: 'user' | 'assistant';
    userName?: string; // For user messages
    content: ReactNode | string;
    timestamp?: string;
}

// Running task text (green text showing LLM actions)
export interface AgenticRunningTask {
    id?: string | number;
    text: string; // Supports **bold** markers
}

// Planner tasks (grey box showing upcoming tasks)
export interface AgenticPlannerTask {
    id?: string | number;
    text: string; // Supports **bold** markers
}

export interface AgenticPlannerTasks {
    id?: string | number;
    tasks: AgenticPlannerTask[];
}

// Phase subtask (individual task within a phase)
export interface AgenticPhaseSubtask {
    id: string;
    title: string;
    description?: string; // Supports **bold** markers
    artefactId?: string;
    status?: 'completed' | 'in_progress' | 'pending';
}

// Phase card (collapsible card with subtasks)
export interface AgenticPhaseCard {
    id: string;
    phaseNumber?: number;
    title: string;
    description?: string; // Supports **bold** markers - middle strip text (e.g. "Executing tasks ...")
    /** Single-line overview content (new format: one liner only) */
    overview?: string;
    subtasks?: AgenticPhaseSubtask[];
    status?: 'completed' | 'in_progress' | 'pending';
    completionSummary?: string; // Supports **bold** markers - shown after phase completion
    isCollapsed?: boolean; // Default collapsed state
    /** task_id for linking to View Artifact / visualization */
    taskId?: string;
}

/** Close match / selected entity for refine-agent disambiguation */
export interface AgenticEntityDisambiguationMatch {
    column_name: string;
    entity_name: string;
}

/** One ambiguous term + its close matches */
export interface AgenticEntityDisambiguationTerm {
    entity_name: string;
    close_matches: AgenticEntityDisambiguationMatch[];
    /**
     * True for an entity in the same question the backend already matched unambiguously
     * (`selected_entities`). Rendered as a collapsed, pre-selected row; does not gate Continue.
     */
    confirmed?: boolean;
}

/** Entity disambiguation card state attached to a conversation pair */
export interface AgenticEntityDisambiguation {
    /** Source message id (for Continue/Skip targeting) */
    messageId?: string | number;
    terms: AgenticEntityDisambiguationTerm[];
    /** Keyed by the ambiguous term's entity_name */
    initialSelections?: Record<string, AgenticEntityDisambiguationMatch>;
    /** entity_names the user marked as "Ignore" — rendered struck-through and excluded from the resume. */
    ignoredTerms?: string[];
    resolved?: boolean;
}

/** Out-of-scope feedback card shown in the left panel (derived from Out of Scope intermediate step). */
export interface AgenticOutOfScopeFeedback {
    title?: string;
    description?: string;
    supportedAnalyses?: string[];
    /**
     * Callback when user submits feedback via the popover.
     * Wired as query-level feedback (`kind: 'summary'`, `run_id` only) — maps to API `feedback_level: "query"`.
     */
    onDislikeFeedbackSubmit?: (payload: DislikeFeedbackSubmitPayload) => void;
    /** Label in dislike popover: Describe the issue in "<value>". */
    dislikeFeedbackContextTitle?: string;
}

/** Per-question-response pair for left panel (derived from AgenticChatMessage) */
export interface AgenticConversationPair {
    userMessage: AgenticConversationMessage;
    assistantMessage: AgenticConversationMessage;
    plannerTasks?: AgenticPlannerTasks;
    phaseCards: AgenticPhaseCard[];
    suggestedActions: SuggestedAction[];
    /** Text to show above followup questions (overview preferred over content) */
    summaryText?: string;
    /** When true, skip the typewriter animation for this pair (e.g. SSE responses with type "ai") */
    disableTypewriter?: boolean;
    /** Refine-agent entity clarification card (interrupt) */
    entityDisambiguation?: AgenticEntityDisambiguation;
    /** Shown instead of assistant narrative when the run completed with an Out of Scope step */
    outOfScopeFeedback?: AgenticOutOfScopeFeedback;
}

// Legacy support - keeping for backward compatibility
export interface AgenticAnalysisStepCard {
    id: string;
    title: string;
    description?: string;
    keyFinding?: string;
    status?: 'completed' | 'in_progress';
    artefactId?: string;
    phaseTitle?: string;
    statusMessage?: string;
}

export interface AgenticPhaseCompleteStatus {
    summary: string;
    details?: string;
}

export interface AgenticHITLAction {
    label: string;
    onClick: () => void;
    primary?: boolean;
}

export interface AgenticHITLCard {
    id?: string | number;
    type?: string;
    title?: string;
    message?: string;
    content?: ReactNode | string;
    actions?: AgenticHITLAction[];
}

export interface SuggestedAction {
    id: string;
    /** Display label (e.g. keyword from followup_questions) */
    label: string;
    /** Full question to populate in input bar when clicked */
    question?: string;
    onClick?: () => void;
}

export interface AgenticLeftPanelProps extends LookupIntegrationProps {
    // Header props
    headerTitle?: string;
    onHeaderActionClick?: () => void;
    /** When true (e.g. disableNavbar mode), shows the sidebar toggle (hamburger) to the left of the title container */
    showSidebarToggleInPanel?: boolean;
    /** Callback when sidebar toggle is clicked - used when showSidebarToggleInPanel is true */
    onSidebarToggleInPanel?: () => void;

    /** Conversation title displayed as a card at the top of the scrollable area (standalone mode) */
    conversationTitle?: string;

    /**
     * When true, renders the conversation title as the blue shadow card (Figma design)
     * instead of as a plain h2 in the header section. Used by AgenticStandaloneLayout
     * so the card is shown alongside the standalone layout's own header.
     */
    showConversationTitleAsCard?: boolean;

    timestamp?: string;
    /** Flat messages (legacy) - used when conversationPairs not provided */
    conversationMessages?: AgenticConversationMessage[];
    /** Per-question-response pairs (preferred when deriving from messages) */
    conversationPairs?: AgenticConversationPair[];

    // New content types for LLM output (legacy - used when conversationPairs not provided)
    runningTasks?: AgenticRunningTask[]; // Green text showing actions
    plannerTasks?: AgenticPlannerTasks; // Grey box with upcoming tasks
    phaseCards?: AgenticPhaseCard[]; // Collapsible phase cards with subtasks

    // Legacy support - keeping for backward compatibility
    analysisSteps?: AgenticAnalysisStepCard[];
    phaseCompleteStatus?: AgenticPhaseCompleteStatus;

    hitlCards?: AgenticHITLCard[];
    showTasks?: boolean;
    onToggleTasks?: (visible: boolean) => void;
    onViewArtefact?: (artefactId: string, stepId: string) => void;
    onTogglePhase?: (phaseId: string, isCollapsed: boolean) => void;

    /**
     * Entity disambiguation Continue — selected matches for the interrupted message.
     * `messageId` identifies the source pair when multiple clarifications exist.
     * `resolvedUserInput` is the original query with ambiguous terms replaced by
     * the chosen close matches — use this as `user_input` for the resume call
     * (do not send a synthetic `"proceed"`).
     */
    onEntityDisambiguationContinue?: (
        selected: AgenticEntityDisambiguationMatch[],
        messageId?: string | number,
        resolvedUserInput?: string,
        /**
         * The chosen match per ambiguous term. Persist it on the interrupt message
         * (`additional_kwargs.clarification_selections`) so a resolved card re-renders with the
         * user's picks — `selected` alone cannot be mapped back to the terms it answers.
         */
        selectionsByTerm?: Record<string, AgenticEntityDisambiguationMatch>,
        /**
         * entity_names the user marked as "Ignore" (they are absent from `selected` and
         * `selectionsByTerm`). Persist as `additional_kwargs.clarification_ignored` so a resolved
         * card re-renders them as ignored, and use them to tell the backend which suggested names
         * to disregard.
         */
        ignoredTerms?: string[],
    ) => void;
    /** Entity disambiguation Skip — resume with empty selected_entities. */
    onEntityDisambiguationSkip?: (messageId?: string | number) => void;

    // Synthesis section
    synthesisItems?: Array<{
        text: string; // Supports **bold** markers
    }>;

    // Assessment section
    assessmentStatus?: string; // e.g., "Assessment Complete!"
    assessmentContent?: string; // Supports **bold** markers

    // Suggested actions
    suggestedActions?: SuggestedAction[];
    /**
     * Callback when a follow-up suggestion pill is clicked.
     * Used for analytics (e.g. LEFT_PANEL_FOLLOW_UP_PILL_CLICK).
     */
    onSuggestedActionClick?: (action: SuggestedAction) => void;
    /**
     * Callback when a phase card's "Show Task" / "Hide Task" toggle is clicked.
     * Used for analytics (e.g. LEFT_PANEL_SHOW_TASK_CLICK). Receives the new
     * collapsed state (true == hidden) and the phase that was toggled.
     */
    onPhaseToggleCollapsed?: (collapsed: boolean, phase: AgenticPhaseCard) => void;

    // Standalone summary (shown in left panel when in standalone/split mode)
    /** Summary text displayed when in standalone/split mode (supports **bold** markdown). Can be a string or an array of sentences. */
    standaloneSummary?: string | string[];
    /** ISO 8601 timestamp shown centered above AskAsper branding in the standalone summary card */
    standaloneSummaryTimestamp?: string;
    /** Follow-up question strings shown as pill buttons below the standalone summary */
    standaloneFollowUpQuestions?: string[];

    // Bottom content slot (rendered at the end of scrollable area, before input bar)
    /** Custom content rendered at the bottom of the scrollable conversation area */
    bottomContent?: ReactNode;

    // Input bar props
    inputValue?: string;
    onInputChange?: (value: string) => void;
    onInputSubmit?: (value: string) => void;
    onQuestionBankClick?: () => void;
    onSaveAsCustom?: (value: string) => void;
    inputPlaceholder?: string;
    /** Hard-disable the full composer (textarea + Send). Host-driven blocking sync only. */
    inputDisabled?: boolean;
    /**
     * When true, Send (and Enter-to-send) is frozen but the textarea stays editable
     * so the user can draft the next message while a response is generating.
     */
    isLoading?: boolean;
    /** Rendered below the bottom input card (e.g. enterprise / review disclaimer in chat view) */
    searchDisclaimer?: ReactNode;

    /** When true, animates conversation and summary text with a typewriter effect. Default: true */
    enableTypewriter?: boolean;
    /** Delay in ms per character for typewriter. Default: 25 */
    typewriterSpeed?: number;

    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Right Panel Types =====

// Finding text segment for KeyFindings
export type FindingTextType = 'regular' | 'bold' | 'label' | 'positive' | 'negative' | 'orange';

export interface FindingTextSegment {
    text: string;
    type?: FindingTextType;
}

export interface FindingItem {
    segments: FindingTextSegment[];
}

// Recommendation text segment
export type RecommendationTextType = 'regular' | 'bold' | 'positive' | 'negative';

export interface RecommendationTextSegment {
    text: string;
    type?: RecommendationTextType;
}

export interface RecommendationItem {
    id?: string;
    segments: RecommendationTextSegment[];
    onClick?: () => void;
}

// Takeaway text segment for KeyTakeaways
export interface TakeawayTextSegment {
    text: string;
    isBold?: boolean;
}

export interface TakeawayItem {
    segments: TakeawayTextSegment[];
}

// Methodology section
export interface MethodologySection {
    title: string;
    items: string[];
}

// Accordion content types
export type AccordionContentType = 'analysisSummary' | 'chartVisualization';

export interface AnalysisSummaryContent {
    type: 'analysisSummary';
    keyFindings?: {
        title?: string;
        items: FindingItem[];
    };
    recommendations?: {
        title?: string;
        items: RecommendationItem[];
    };
    /** Key takeaways (from Executing step key_takeaways) - shown below key findings */
    keyTakeaways?: {
        title?: string;
        items: TakeawayItem[];
    };
    /** Methodology (formulae from Executing step) - shown separately from summary */
    methodology?: {
        title?: string;
        sections: MethodologySection[];
        formula?: string | string[];
    };
}

/** One scenario a user created for a simulation - rendered as a hyperlink below its table */
export interface ScenarioLink {
    url: string;
    scenario_id?: number | string;
    scenario_label?: string;
}

/** Single viz item when multiple visualizations share the same task accordion */
export interface ChartVisualizationItem extends AgenticHtmlChartFields {
    chartType?: 'bar' | 'line' | 'area' | 'bpl' | 'table' | 'waterfall' | 'stackbar' | 'horizontalbar';
    vizData: any;
    /** Per-visualization scenario link (e.g. table-link) - shown below each table when present */
    scenarioLink?: string;
    /** Per-visualization scenario links (one per created scenario) - shown below each table when present, takes precedence over scenarioLink */
    scenarioLinks?: ScenarioLink[];
}

export interface ChartVisualizationContent extends AgenticHtmlChartFields {
    type: 'chartVisualization';
    keyTakeaways?: {
        title?: string;
        items: TakeawayItem[];
    };
    chartType?: 'bar' | 'line' | 'area' | 'bpl' | 'table' | 'waterfall' | 'stackbar' | 'horizontalbar';
    vizData?: any; // Compatible with EnhancedBarChart/EnhancedLineChart VizData (single viz - legacy)
    /** Multiple visualizations for same task (chart + table) - when present, takes precedence over vizData */
    visualizations?: ChartVisualizationItem[];
    customContent?: ReactNode; // For custom visualizations
    /** Scenario link URL - shown after table, above methodology (from task_complete visualizations) */
    scenarioLink?: string;
    /** Scenario links (one per created scenario) - shown after table, above methodology; takes precedence over scenarioLink */
    scenarioLinks?: ScenarioLink[];
    methodology?: {
        title?: string;
        sections: MethodologySection[];
        formula?: string | string[];
    };
}

export type AccordionContent = AnalysisSummaryContent | ChartVisualizationContent;

/** Payload for messages-mode right-panel feedback hooks (see deriveLayoutFromMessages). */
export type RightPanelFeedbackPayload =
    | {
          kind: 'summary';
          messageIndex: number;
          questionTitle: string;
          accordionItemId: string;
          run_id?: string;
          session_id?: string;
      }
    | {
          kind: 'task';
          messageIndex: number;
          questionTitle: string;
          taskId: string;
          taskTitle: string;
          accordionItemId: string;
          run_id?: string;
          session_id?: string;
      };

/** Text and optional file attachments from the dislike feedback popover. */
export interface DislikeFeedbackSubmitPayload {
    feedback: string;
    files?: File[];
}

export type RightPanelDislikeFeedbackPayload = RightPanelFeedbackPayload & DislikeFeedbackSubmitPayload;

export interface DeriveLayoutFeedbackHandlers {
    onRightPanelThumbsUp?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelThumbsDown?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelDislikeFeedbackSubmit?: (payload: RightPanelDislikeFeedbackPayload) => void;
}

/** Single stored feedback row for a run (query-level uses run_id; task artefact uses task_id). */
export interface AgenticFeedbackEntry {
    run_id?: string;
    task_id?: string;
    thumbs_up: 'like' | 'dislike' | 'none';
    subjective_answer?: string;
}

export interface AgenticFeedbackRunRow {
    run_id: string;
    feedback: AgenticFeedbackEntry[];
}

/** Thread feedback history from the host (e.g. loaded when opening a thread). */
export interface AgenticFeedbackHistoryResponse {
    thread_id: string;
    runs: AgenticFeedbackRunRow[];
}

// Child accordion item (nested inside a group accordion)
export interface AccordionMenuItem {
    key: string;
    label: string;
    icon?: ReactNode;
    disabled?: boolean;
    onClick?: () => void;
}

export interface AgenticChildAccordionItem {
    id: string;
    run_id?: string;
    session_id?: string;
    title: string;
    subtitle?: string;
    defaultExpanded?: boolean;
    content: AccordionContent;
    /**
     * When loading prior feedback, sets thumbs UI to match stored like/dislike.
     * Omitted when unknown; derived in messages mode when `feedbackHistory` is passed to the layout.
     */
    initialFeedbackVote?: 'up' | 'down' | null;
    // Actions
    onThumbsDown?: () => void;
    onThumbsUp?: () => void;
    /** Callback when dislike feedback is submitted via the popover */
    onDislikeFeedbackSubmit?: (payload: DislikeFeedbackSubmitPayload) => void;
    /**
     * Label in dislike popover: Describe the issue in "<dislikeFeedbackContextTitle>".
     * For the Summary child under a group, use the **parent** question heading (AgenticRightPanel
     * falls back to the parent accordion title when this is omitted). For task/chart rows, defaults to `title`.
     */
    dislikeFeedbackContextTitle?: string;
    /** Menu items for the dots menu dropdown */
    menuItems?: AccordionMenuItem[];
}

// Accordion item for right panel - can be a regular item or a group containing nested accordions
export interface AgenticAccordionItem {
    id: string;
    run_id?: string;
    session_id?: string;
    title: string;
    /** Optional numbered index (e.g., "1." displayed before title) */
    index?: number;
    defaultExpanded?: boolean;
    /** Content for regular accordion items (mutually exclusive with children) */
    content?: AccordionContent;
    /** Child accordion items for group accordions (mutually exclusive with content) */
    children?: AgenticChildAccordionItem[];
    /** Same semantics as {@link AgenticChildAccordionItem.initialFeedbackVote} (e.g. single-artefact row). */
    initialFeedbackVote?: 'up' | 'down' | null;
    // Actions
    onThumbsDown?: () => void;
    onThumbsUp?: () => void;
    /** Callback when dislike feedback is submitted via the popover */
    onDislikeFeedbackSubmit?: (payload: DislikeFeedbackSubmitPayload) => void;
    dislikeFeedbackContextTitle?: string;
}

export interface AgenticArtifact extends AgenticHtmlChartFields {
    id: string;
    title: string;
    type: 'chart' | 'table' | 'heatmap' | 'other';
    chartType?: 'bar' | 'line' | 'area' | 'bpl' | 'table' | 'waterfall' | 'stackbar' | 'horizontalbar';
    // Visualization data - can be VizData format or custom ReactNode
    vizData?: any; // Compatible with EnhancedBarChart/EnhancedLineChart VizData
    customContent?: ReactNode; // For custom visualizations
    // Insight box content
    insight?: {
        icon?: ReactNode;
        text: string; // Supports **bold** markers
    };
    // Chart controls/filters
    filters?: Array<{
        label: string;
        options: Array<{ label: string; value: string | number }>;
        defaultValue?: string | number;
    }>;
    // Footer link
    methodologyLink?: {
        label: string;
        onClick: () => void;
    };
    // Actions
    onClose?: () => void;
    onShare?: () => void;
    onDownload?: () => void;
}

export interface AgenticAnalysisSummary {
    /** Markdown content for the summary (rendered like AnalysisSteps when present). Can be a string or an array of sentences. */
    content?: string | string[];
    keyFindings?: Array<{
        text: string; // Supports **bold** markers
    }>;
    additionalInsight?: {
        icon?: ReactNode;
        text: string; // Supports **bold** markers
    };
    recommendations?: string[];
    onClose?: () => void;
    onShare?: () => void;
}

/** Allows hosts to return a Promise so export progress UI can await API work. */
export type AgenticExportCallbackResult = void | Promise<void>;

/** Ordered block for right panel - summary then artifacts per message/chat */
export interface RightPanelBlock {
    messageIndex: number;
    /** User's chat query for this message (e.g. `user_input`) — used for export traceability */
    userQuery?: string;
    /** Run id for this message's response — used for per-question export deep links and scroll. */
    runId?: string;
    summary?: AgenticAnalysisSummary;
    /**
     * When true, show loading state for analysis summary until summary is ready.
     * With a grouped (full-response) accordion, renders as the first item inside that accordion body; otherwise below block content.
     */
    summaryLoading?: boolean;
    accordionItems: AgenticAccordionItem[];
}

/** Host-provided base URL for deep links appended below tables in PDF export. */
export interface AgenticExportDeepLinkConfig {
    /** Application origin, e.g. `https://app.example.com` */
    hostLink: string;
    /** Route path appended to hostLink, e.g. `/ask-asper/chat` */
    endpoint: string;
    /**
     * Already-obfuscated current user id (see `encodeAgenticDeepLinkUser`). When set, it is
     * appended as `&userId=…` to every generated deep link so the opener can be verified.
     */
    encodedUserId?: string;
}

/**
 * Parsed deep link target from URL query params. Always has `threadId` and exactly one of
 * `taskId` (per-table artefact) or `runId` (per-question response). `encodedUserId` is the
 * obfuscated owner id used for an ownership check by the host.
 */
export interface AgenticExportDeepLinkTarget {
    threadId: string;
    /** Per-table artefact id (mutually exclusive with `runId`). */
    taskId?: string;
    /** Per-question response id (mutually exclusive with `taskId`). */
    runId?: string;
    /** Obfuscated user id from the `userId` query param (see `decodeAgenticDeepLinkUser`). */
    encodedUserId?: string;
}

export interface AgenticRightPanelProps {
    /** Ordered blocks (summary + artifacts per message) - preferred when using messages mode */
    rightPanelBlocks?: RightPanelBlock[];
    /** Accordion items to render in the panel (legacy / when rightPanelBlocks not provided) */
    accordionItems?: AgenticAccordionItem[];
    /** Legacy: artifacts array (for backward compatibility) */
    artifacts?: AgenticArtifact[];
    activeArtifactId?: string;
    /** Summaries (one per message) - preferred when using messages mode */
    summaries?: AgenticAnalysisSummary[];
    /** Legacy: single summary (mapped to [summary] when summaries not provided) */
    summary?: AgenticAnalysisSummary;
    onArtifactSelect?: (artifactId: string) => void;
    onArtifactClose?: (artifactId: string) => void;
    /** Whether multi-select mode is enabled */
    isMultiSelectMode?: boolean;
    /** Callback when multi-select mode is toggled */
    onToggleMultiSelect?: () => void;
    /** Array of selected accordion/artifact IDs */
    selectedArtifactIds?: string[];
    /** Callback when accordion/artifact selection changes */
    onArtifactSelectionChange?: (artifactId: string, selected: boolean) => void;
    /** Callback when "Select all" is clicked in multi-select strip. Receives the resolved list of
     *  artefact IDs that were newly selected (excluding already-selected items). */
    onSelectAll?: (newlySelectedIds: string[]) => void;
    /** Callback when "Deselect all" is clicked in multi-select strip. Receives the IDs that were
     *  cleared from the selection. */
    onDeselectAll?: (clearedIds: string[]) => void;
    /** Callback when an artefact's "View Methodology" section is expanded or collapsed.
     *  Used by hosts for analytics (e.g. RIGHT_PANEL_METHODOLOGY_EXPAND_CLICK /
     *  RIGHT_PANEL_METHODOLOGY_COLLAPSE_CLICK). */
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void;
    /** Callback when the per-artefact export/menu trigger button is clicked (the dots/export
     *  icon next to the feedback buttons in each artefact accordion). Used by hosts for
     *  analytics (e.g. RIGHT_PANEL_ARTEFACT_EXPORT_CLICK). */
    onArtefactMenuTriggerClick?: (artefactTitle: string) => void;
    /** Callback when a specific export option (Word / PDF / PPT / Excel variants) is clicked for a
     *  single artefact. Used by hosts for analytics (e.g. RIGHT_PANEL_ARTEFACT_EXPORT_OPTION_CLICK).
     *  `exportOption` is a stable identifier such as `docx` | `pdf` | `ppt` | `xlsx_complete_dataset`
     *  | `xlsx_current_view`. */
    onArtefactExportOptionClick?: (exportOption: string, artefactTitle: string) => void;
    /** Callback when a specific export option is clicked while in multi-select mode. Used by hosts for
     *  analytics (e.g. RIGHT_PANEL_MULTI_SELECT_EXPORT_OPTION_CLICK). `exportOption` uses the same
     *  identifiers as {@link onArtefactExportOptionClick}. */
    onMultiSelectExportOptionClick?: (exportOption: string) => void;
    /** Generic chart-interaction callback forwarded to all chart visualizations
     *  (EnhancedBarChart / EnhancedLineChart / EnhancedStackedBarChart / EnhancedHorizontalBarChart).
     *  Used by hosts for analytics (e.g. RIGHT_PANEL_CHART_*). Pair with
     *  `createChartEventForwarder` from `mixpanelEvents` to map to AskAsper events. */
    onTrackChartEvent?: import('../../molecules/EnhancedBarChart/EnhancedBarChart.types').ChartInteractionEventCallback;
    /** Callback when close button is clicked */
    onClose?: () => void;
    /**
     * Callback when Excel export is selected from the list panel (API). PDF, Word, and PPT are
     * handled client-side in the panel. Return a Promise if the export is async so the progress UI can await completion.
     * For multi-select “Export complete dataset” (xlsx), the panel calls this once per distinct
     * `(session_id, run_id)` pair derived from the selected artefacts (same as a single message’s export).
     */
    onArtifactExport?: (
        format: 'docx' | 'xlsx' | 'pdf' | 'ppt',
        run_id?: string,
        session_id?: string,
    ) => AgenticExportCallbackResult;
    /**
     * Callback when multi-select export is triggered. Excel is typically API-based; PDF, Word, and
     * PPT multi-select exports are handled client-side first, then this fires for bookkeeping.
     * Return a Promise for async Excel export so the progress UI can await completion.
     * For xlsx “complete dataset”, prefer `onArtifactExport`: it receives deduped run/session pairs.
     * This is only used for xlsx when `onArtifactExport` is not provided (legacy).
     */
    onMultiSelectExport?: (
        format: 'docx' | 'xlsx' | 'pdf' | 'ppt',
        selectedIds: string[],
    ) => AgenticExportCallbackResult;
    /** Callback when Create Case is selected from accordion dots menu */
    onCreateCase?: () => void;
    /** When true (e.g. disableNavbar mode), shows the focus mode toggle (list/presentation) in the top right of the header */
    showFocusModeToggleInPanel?: boolean;
    /** Current view mode - used when showFocusModeToggleInPanel is true */
    viewMode?: 'list' | 'presentation';
    /** Callback when focus mode changes - used when showFocusModeToggleInPanel is true */
    onViewModeChangeInPanel?: (mode: 'list' | 'presentation') => void;
    /** When this increases (new question asked), re-enable auto-scroll after user has scrolled up */
    conversationPairCount?: number;
    /** Conversation/thread title from the left panel - used in PPT cover slide metadata */
    conversationTitle?: string;
    /**
     * When set with {@link threadId}, a deep link is appended below each table in PDF export
     * pointing back to the live chat artefact (`?threadId=…&taskId=…`).
     */
    exportDeepLink?: AgenticExportDeepLinkConfig;
    /** Current conversation thread id — used as `threadId` query param in export deep links. */
    threadId?: string;
    /**
     * When set, expands the matching artefact accordion and scrolls it into view in the right panel.
     * Typically driven by {@link AgenticTwoPanelLayoutProps.exportDeepLinkTarget} after the thread loads.
     */
    deepLinkTaskId?: string;
    /** Fired after a {@link deepLinkTaskId} scroll attempt completes (`found` = element was located). */
    onDeepLinkTaskScrollComplete?: (taskId: string, found: boolean) => void;
    /**
     * When set, expands/scrolls the matching question response block (`[data-run-id]`) into view.
     * Driven by a `runId` export deep link target.
     */
    deepLinkRunId?: string;
    /** Fired after a {@link deepLinkRunId} scroll attempt completes (`found` = element was located). */
    onDeepLinkRunScrollComplete?: (runId: string, found: boolean) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Two Panel Layout Types =====
export interface AgenticTwoPanelLayoutProps extends LookupIntegrationProps {
    /** When provided, layout derives left/right panel state from messages + loadingMessage */
    messages?: AgenticChatMessage[];
    /** Currently loading/streaming message (SSE updates applied by parent via processSSEMessage) */
    loadingMessage?: AgenticChatMessage | null;
    /** Send user question (e.g. from input box); parent starts SSE and updates messages/loadingMessage */
    onSendMessage?: (message: string) => void;
    /** Stop generating the current loading response */
    onStopGenerating?: () => void;

    // Left Panel Props (used when messages not provided, or as overrides)
    leftPanelHeaderTitle?: string;
    onLeftPanelHeaderAction?: () => void;
    leftPanelTimestamp?: string;
    conversationMessages?: AgenticConversationMessage[];
    conversationPairs?: AgenticConversationPair[];

    // New content types for LLM output
    runningTasks?: AgenticRunningTask[]; // Green text showing actions
    plannerTasks?: AgenticPlannerTasks; // Grey box with upcoming tasks
    phaseCards?: AgenticPhaseCard[]; // Phase cards with subtasks (new format)
    onTogglePhase?: (phaseId: string, isCollapsed: boolean) => void;
    hitlCards?: AgenticHITLCard[];
    onViewArtefact?: (artefactId: string, stepId: string) => void;
    onEntityDisambiguationContinue?: (
        selected: AgenticEntityDisambiguationMatch[],
        messageId?: string | number,
        resolvedUserInput?: string,
        /**
         * The chosen match per ambiguous term. Persist it on the interrupt message
         * (`additional_kwargs.clarification_selections`) so a resolved card re-renders with the
         * user's picks — `selected` alone cannot be mapped back to the terms it answers.
         */
        selectionsByTerm?: Record<string, AgenticEntityDisambiguationMatch>,
        /**
         * entity_names the user marked as "Ignore" (they are absent from `selected` and
         * `selectionsByTerm`). Persist as `additional_kwargs.clarification_ignored` so a resolved
         * card re-renders them as ignored, and use them to tell the backend which suggested names
         * to disregard.
         */
        ignoredTerms?: string[],
    ) => void;
    onEntityDisambiguationSkip?: (messageId?: string | number) => void;

    // Assessment section
    assessmentStatus?: string; // e.g., "Assessment Complete!"
    assessmentContent?: string; // Supports **bold** markers

    // Suggested actions
    suggestedActions?: SuggestedAction[];

    // Legacy support (deprecated - use phaseCards instead)
    analysisSteps?: AgenticAnalysisStepCard[];
    phaseCompleteStatus?: AgenticPhaseCompleteStatus;
    showTasks?: boolean;
    onToggleTasks?: (visible: boolean) => void;

    // Right Panel Props
    /** Accordion items to render in the right panel (data-driven approach) */
    accordionItems?: AgenticAccordionItem[];
    /** Legacy: artifacts array (for backward compatibility) */
    artifacts?: AgenticArtifact[];
    activeArtifactId?: string;
    /** Summaries (one per message) - used when deriving from messages */
    summaries?: AgenticAnalysisSummary[];
    /** Legacy: single summary (used when not in messages mode) */
    summary?: AgenticAnalysisSummary;
    onArtifactSelect?: (artifactId: string) => void;
    onArtifactClose?: (artifactId: string) => void;
    /** Whether multi-select mode is enabled */
    isMultiSelectMode?: boolean;
    /** Callback when multi-select mode is toggled */
    onToggleMultiSelect?: () => void;
    /** Array of selected accordion/artifact IDs */
    selectedArtifactIds?: string[];
    /** Callback when accordion/artifact selection changes */
    onArtifactSelectionChange?: (artifactId: string, selected: boolean) => void;
    /** Callback when close button is clicked */
    onRightPanelClose?: () => void;
    /** Messages mode: wired to derived summary/task accordions */
    onRightPanelThumbsUp?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelThumbsDown?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelDislikeFeedbackSubmit?: (payload: RightPanelDislikeFeedbackPayload) => void;
    /**
     * When set with messages mode, summary and artefact accordions show prior like/dislike from the host
     * (same ordering as chat history: one entry per run_id in `runs`).
     */
    feedbackHistory?: AgenticFeedbackHistoryResponse;
    defaultLeftPanelWidth?: number; // Percentage (0-100), default 50
    minLeftPanelWidth?: number; // Percentage, default 30
    maxLeftPanelWidth?: number; // Percentage, default 70
    onPanelResize?: (leftPanelWidth: number) => void;
    /** Optional footer height (px). When provided, the two-panel interface height is reduced so it sits above the consumer's bottom footer. */
    footerHeight?: number;

    // Top Bar Props (optional)
    showTopBar?: boolean;
    /** When true, hides the top bar and moves the sidebar toggle (hamburger) to the left panel and focus mode toggle to the right panel. Use when the host app has its own global navbar. */
    disableNavbar?: boolean;
    breadcrumbs?: BreadcrumbItem[];
    onBreadcrumbClick?: (item: BreadcrumbItem, index: number) => void;
    /** Callback when the Home icon (first breadcrumb) is clicked */
    onHomeClick?: () => void;
    onNotificationClick?: () => void;
    showNotification?: boolean;
    notificationCount?: number;

    // Sidebar Props
    showSidebar?: boolean;
    sidebarCollapsed?: boolean;
    onSidebarToggle?: (collapsed: boolean) => void;
    sidebarThreads?: ConversationThread[];
    /** Whether sidebar threads are loading - shows animated loading bar */
    sidebarThreadsLoading?: boolean;
    sidebarActiveThreadId?: string;
    sidebarUser?: UserProfile;
    onSidebarNewChat?: () => void;
    onSidebarSearchChats?: () => void;
    onSidebarThreadSelect?: (threadId: string) => void;
    onSidebarThreadEdit?: (threadId: string, newName: string) => void;
    onSidebarThreadDelete?: (threadId: string) => void;
    onSidebarUserClick?: () => void;

    // View mode
    viewMode?: 'list' | 'presentation';
    onViewModeChange?: (mode: 'list' | 'presentation') => void;
    showViewModeToggle?: boolean;

    // Action buttons
    onCommentClick?: () => void;
    onCreateCaseClick?: () => void;
    showCommentButton?: boolean;
    showCreateCaseButton?: boolean;
    // Create Case Modal props
    existingCases?: CaseItem[];
    onCaseSave?: (data: CreateCaseSaveData) => void;

    // DotsGrid / Tool Panel
    onDotsGridClick?: () => void;
    showDotsGridButton?: boolean;
    toolPanelOpen?: boolean;
    onToolPanelOpenChange?: (open: boolean) => void;
    /** Callback when Export format is selected from the tool panel popup (PDF, PPT, Excel, Word) */
    onExportClick?: (format: 'pdf' | 'ppt' | 'xlsx' | 'docx') => void;
    /**
     * Artefact export from the right panel: list mode forwards Excel (API); presentation/focus mode
     * forwards Excel and PPT (API). PDF and Word are handled client-side in the panel. Return a
     * Promise if the export is async so the progress UI can await completion.
     * Multi-select “Export complete dataset” (xlsx) invokes this once per distinct `(session_id, run_id)`.
     */
    onArtifactExport?: (
        format: 'docx' | 'xlsx' | 'pdf' | 'ppt',
        run_id?: string,
        session_id?: string,
    ) => AgenticExportCallbackResult;
    /**
     * Callback when multi-select export is triggered. Excel is typically API-based; PDF, Word, and
     * PPT multi-select exports are handled client-side first, then this fires for bookkeeping.
     * Return a Promise for async Excel export so the progress UI can await completion.
     * For xlsx “complete dataset”, prefer `onArtifactExport`: deduped per message `(session_id, run_id)`.
     * Used for xlsx only when `onArtifactExport` is not provided (legacy).
     */
    onMultiSelectExport?: (
        format: 'docx' | 'xlsx' | 'pdf' | 'ppt',
        selectedIds: string[],
    ) => AgenticExportCallbackResult;
    onShareClick?: () => void;
    onPlaceholderClick?: () => void;
    onToolPanelSearch?: (value: string) => void;

    // Question Bank (same as AgenticMainLayout / AgenticLandingContent)
    onQuestionBankClick?: () => void;
    onSaveAsCustom?: (value: string) => void;
    /** Question bank categories in API format: { Category, Questions: [{ QuestionID, Question }] }[] */
    questionBankCategories?: QuestionBankCategoryFromAPI[];

    // Custom questions - endpoint support for save, edit, delete (same as AskAsperChatInterface)
    customQuestions?: Array<{ id: string; text: string }>;
    onAddCustomQuestion?: (question: { id: string; text: string }) => void;
    onEditCustomQuestion?: (question: {
        id?: string | number;
        text?: string;
        QuestionID?: number;
        Question?: string;
    }) => void;
    onDeleteCustomQuestion?: (questionId: string) => void;
    /** Disable the conversation input, e.g. while the host is completing a blocking sync. */
    inputDisabled?: boolean;
    /** Below the left-panel input bar while in conversation mode (not used on landing layout) */
    searchDisclaimer?: ReactNode;

    // ===== Mixpanel Analytics =====
    /**
     * Analytics callback. Receives `(eventName, eventProperty)` where `eventName`
     * is one of the constants in `utils/mixpanelEvents.ts`. Matches the host
     * app's `trackEvent(eventName, eventProperty)` signature.
     */
    onTrackEvent?: import('./utils/mixpanelEvents').MixpanelTrackEventCallback;

    /**
     * When set with {@link exportThreadId} (or {@link sidebarActiveThreadId}), deep links are
     * appended below tables in PDF export pointing back to the live chat artefact.
     */
    exportDeepLink?: AgenticExportDeepLinkConfig;
    /** Thread id for export deep links. Falls back to {@link sidebarActiveThreadId} when omitted. */
    exportThreadId?: string;
    /**
     * Deep link target parsed from the host route (e.g. PDF export link opened in the browser).
     * When present, the layout calls {@link onSidebarThreadSelect} for `threadId`, then scrolls
     * the right panel to `taskId` once that thread's messages are loaded.
     */
    exportDeepLinkTarget?: AgenticExportDeepLinkTarget | null;
    /**
     * Called after the layout has scrolled to {@link exportDeepLinkTarget}'s `taskId` successfully,
     * or given up after retries. Host should clear URL query params here.
     */
    onExportDeepLinkConsumed?: (target: AgenticExportDeepLinkTarget, found: boolean) => void;

    'data-test'?: string;
    'data-test-id'?: string;
}

// ===== Standalone Layout Types =====

/**
 * View modes for the AgenticStandaloneLayout:
 * - 'standalone': Only the right panel is visible with standalone content. No left panel.
 * - 'split': Both panels visible. Left = Ask Asper/Agentic. Right = Standalone content. Fixed layout (no drag).
 * - 'artifact': Both panels visible. Left = Ask Asper/Agentic. Right = Artifact view. Draggable/resizable.
 */
export type StandaloneViewMode = 'standalone' | 'split' | 'artifact';

export interface AgenticStandaloneLayoutProps {
    // ===== View State =====
    /** Current view mode of the layout */
    viewMode?: StandaloneViewMode;
    /** Callback when view mode changes */
    onViewModeChange?: (mode: StandaloneViewMode) => void;

    // ===== Messages-driven mode (derives left/right panel from messages + loadingMessage) =====
    /** When provided, layout derives left/right panel state from messages + loadingMessage */
    messages?: AgenticChatMessage[];
    /** Currently loading/streaming message (SSE updates applied by parent via processSSEMessage) */
    loadingMessage?: AgenticChatMessage | null;
    /** Send user question (e.g. from input box); parent starts SSE and updates messages/loadingMessage.
     *  The second parameter `newSession` is `true` for the very first send after mount/reset, `false` afterwards. */
    onSendMessage?: (message: string, newSession: boolean) => void;
    /** Stop generating the current loading response */
    onStopGenerating?: () => void;

    // ===== Standalone Content (Right Panel Default) =====
    /** The standalone component to render in the right panel (default & split modes) */
    standaloneContent?: ReactNode;

    // ===== Left Panel Props (Ask Asper / Agentic) =====
    /** Header title for the left panel */
    leftPanelHeaderTitle?: string;
    /** Callback for header action button in left panel */
    onLeftPanelHeaderAction?: () => void;
    /** Conversation title displayed as a card at the top of the scrollable area */
    leftPanelConversationTitle?: string;
    /** Timestamp for the left panel header */
    leftPanelTimestamp?: string;
    /** Conversation messages for the left panel (used when messages not provided) */
    conversationMessages?: AgenticConversationMessage[];
    /** Per-question-response pairs (used when messages not provided, or derived from messages) */
    conversationPairs?: AgenticConversationPair[];
    /** Running tasks (green text showing LLM actions) */
    runningTasks?: AgenticRunningTask[];
    /** Planner tasks (grey box showing upcoming tasks) */
    plannerTasks?: AgenticPlannerTasks;
    /** Phase cards with subtasks */
    phaseCards?: AgenticPhaseCard[];
    /** Callback when a phase card is toggled */
    onTogglePhase?: (phaseId: string, isCollapsed: boolean) => void;
    /** HITL (Human in the Loop) interrupt cards */
    hitlCards?: AgenticHITLCard[];
    /** Callback when "View Artefact" is clicked inside the left panel */
    onViewArtefact?: (artefactId: string, stepId: string) => void;
    /** Assessment status text */
    assessmentStatus?: string;
    /** Assessment content (supports **bold** markers) */
    assessmentContent?: string;
    /** Suggested follow-up actions */
    suggestedActions?: SuggestedAction[];

    // ===== Legacy Left Panel Props (deprecated) =====
    analysisSteps?: AgenticAnalysisStepCard[];
    phaseCompleteStatus?: AgenticPhaseCompleteStatus;
    showTasks?: boolean;
    onToggleTasks?: (visible: boolean) => void;

    // ===== Right Panel Props (Artifact View) =====
    /** Accordion items for the artifact right panel */
    accordionItems?: AgenticAccordionItem[];
    /** Legacy: artifacts array */
    artifacts?: AgenticArtifact[];
    /** Active artifact ID */
    activeArtifactId?: string;
    /** Analysis summary (used when not in messages mode) */
    summary?: AgenticAnalysisSummary;
    /** Summaries (one per message) - used when deriving from messages */
    summaries?: AgenticAnalysisSummary[];
    /** When in artifact mode: 'list' shows accordion, 'presentation' shows focus/carousel panel */
    artifactViewMode?: 'list' | 'presentation';
    /** Callback when artifact view mode changes */
    onArtifactViewModeChange?: (mode: 'list' | 'presentation') => void;
    /** Callback when an artifact is selected */
    onArtifactSelect?: (artifactId: string) => void;
    /** Callback when an artifact is closed */
    onArtifactClose?: (artifactId: string) => void;
    /** Whether multi-select mode is enabled */
    isMultiSelectMode?: boolean;
    /** Callback when multi-select mode is toggled */
    onToggleMultiSelect?: () => void;
    /** Array of selected artifact IDs */
    selectedArtifactIds?: string[];
    /** Callback when artifact selection changes */
    onArtifactSelectionChange?: (artifactId: string, selected: boolean) => void;
    /** Callback when right panel close is clicked */
    onRightPanelClose?: () => void;
    /** Messages mode: wired to derived summary/task accordions */
    onRightPanelThumbsUp?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelThumbsDown?: (payload: RightPanelFeedbackPayload) => void;
    onRightPanelDislikeFeedbackSubmit?: (payload: RightPanelDislikeFeedbackPayload) => void;
    /** See {@link AgenticTwoPanelLayoutProps.feedbackHistory}. */
    feedbackHistory?: AgenticFeedbackHistoryResponse;

    // ===== Right Panel Topbar Props =====
    /** Callback when the "Create Case" button on the right panel topbar is clicked */
    onRightPanelCreateCaseClick?: () => void;
    /** Whether the "Create Case" button is shown in the right panel topbar (default: true) */
    showRightPanelCreateCaseButton?: boolean;
    /** Existing cases for the Create Case modal */
    existingCases?: CaseItem[];
    /** Callback when a case is saved from the Create Case modal */
    onCaseSave?: (data: CreateCaseSaveData) => void;
    /** Callback when the dots-grid button on the right panel topbar is clicked */
    onRightPanelDotsGridClick?: () => void;
    /** Controlled open state for the right panel tool panel popover */
    rightPanelToolPanelOpen?: boolean;
    /** Callback when the right panel tool panel popover open state changes */
    onRightPanelToolPanelOpenChange?: (open: boolean) => void;
    /** Callback when the export button in the right panel tool panel is clicked */
    /** Callback when Export format is selected from the tool panel popup (PDF, PPT, Excel, Word) */
    onRightPanelExportClick?: (format: 'pdf' | 'ppt' | 'xlsx' | 'docx') => void;
    /** Callback when the share button in the right panel tool panel is clicked */
    onRightPanelShareClick?: () => void;
    /** Callback when the placeholder button in the right panel tool panel is clicked */
    onRightPanelPlaceholderClick?: () => void;
    /** Callback when searching within the right panel tool panel */
    onRightPanelToolPanelSearch?: (query: string) => void;

    // ===== Standalone Summary Props =====
    /** Summary text displayed in the left panel when in standalone/split mode (supports **bold** markdown). Can be a string or an array of sentences. */
    standaloneSummary?: string | string[];
    /** ISO 8601 timestamp shown centered above AskAsper branding in the standalone summary card */
    standaloneSummaryTimestamp?: string;
    /** Follow-up question strings shown as pill buttons below the standalone summary */
    standaloneFollowUpQuestions?: string[];

    // ===== Layout Configuration =====
    /** Default left panel width percentage for artifact/draggable mode (default: 40) */
    defaultLeftPanelWidth?: number;
    /** Minimum left panel width percentage (default: 25) */
    minLeftPanelWidth?: number;
    /** Maximum left panel width percentage (default: 65) */
    maxLeftPanelWidth?: number;
    /** Callback during panel resize */
    onPanelResize?: (leftPanelWidth: number) => void;
    /** Fixed left panel width percentage for split mode (default: 33) */
    splitLeftPanelWidth?: number;

    // ===== Input Bar Props =====
    /** Controlled input value */
    inputValue?: string;
    /** Callback when input value changes */
    onInputChange?: (value: string) => void;
    /** Callback when input is submitted.
     *  The optional second parameter `newSession` is `true` for the very first send after mount/reset, `false` afterwards.
     *  (Only populated when using the standalone layout's internal new_session tracking.) */
    onInputSubmit?: (value: string, newSession?: boolean) => void;
    /** Callback when Question Bank button is clicked */
    onQuestionBankClick?: () => void;
    /** Callback when Save as Custom is clicked */
    onSaveAsCustom?: (value: string) => void;
    /** Placeholder text for the input */
    inputPlaceholder?: string;
    /** Whether the input is disabled */
    inputDisabled?: boolean;

    'data-test'?: string;
    'data-test-id'?: string;
}
